import type { ReactNode } from 'react';
import { FloatingMenu } from '../../FloatingMenu';
import type { PickerMenuAlign } from '../../shared/picker/PickerMenuShell';
import { usePickerMenu } from '../../shared/picker/usePickerMenu';
import { PriorityMenu } from './PriorityMenu.js';
import { PriorityTriggerButton } from './PriorityTrigger.js';
import type { PriorityLevel } from './priority.js';

interface PriorityPickerProps {
    value: PriorityLevel;
    onSelect: (level: PriorityLevel, reason?: string) => void;
    align?: PickerMenuAlign;
    dropUp?: boolean;
    heading?: string;
    requireExtremeReason?: boolean;
    ariaLabel?: string;
    tooltip?: ReactNode;
    compact?: boolean;
}

// Presentational: the caller owns the value and what a pick does.
export function PriorityPicker({
    value,
    onSelect,
    align = 'left',
    dropUp = false,
    heading,
    requireExtremeReason = true,
    ariaLabel,
    tooltip,
    compact
}: PriorityPickerProps) {
    const menu = usePickerMenu();

    const handleSelect = (level: PriorityLevel, reason?: string) => {
        menu.closeToTrigger();
        onSelect(level, reason);
    };

    return (
        <div
            ref={menu.rootRef}
            onKeyDown={menu.onKeyDown}
            // min-w-0 lets a compact trigger shrink inside the AI marker's flex wrapper.
            className={compact ? 'min-w-0' : undefined}
        >
            <PriorityTriggerButton
                level={value}
                ariaLabel={ariaLabel}
                tooltip={tooltip}
                compact={compact}
                onClick={(e) => {
                    e.stopPropagation();
                    menu.toggle();
                }}
            />
            <FloatingMenu
                open={menu.open}
                triggerRef={menu.rootRef}
                onClose={menu.close}
                align={align}
                preferUp={dropUp}
            >
                <PriorityMenu
                    current={value}
                    onSelect={handleSelect}
                    heading={heading}
                    requireExtremeReason={requireExtremeReason}
                />
            </FloatingMenu>
        </div>
    );
}
